import React, { useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { Button, notification, Spin, Modal, Collapse, Switch, Select, Tooltip, Input, Upload, Progress, Icon, Row, Col } from 'antd'
import { getCausasPenalesDetailPromise, excelCategoriasDelitosPromise, updateCategoriaDelitoPromise, uploadCategoriasDelitosPromise } from '../../promises'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import './CategoriaDelito.scss'

const CategoriaDelito = ({ visible, onCancel, t }) => {
  const [isLoadingCausasPenales, setIsLoadingCausasPenales] = useState(true)
  const [causasPenalesData, setCausasPenalesData] = useState(null)
  const [causasPenales, setCausasPenales] = useState([])
  const [searchFilter, setSearchFilter] = useState('')
  const [expandedKeys, setExpandedKeys] = useState([])
  const [lastUpdate, setLastUpdate] = useState(null)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false)
  const [isUploadingCategories, setIsUploadingCategories] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadModalVisible, setUploadModalVisible] = useState(false)
  const [loadingItems, setLoadingItems] = useState({})
  const [selectedFile, setSelectedFile] = useState(null)

  useEffect(() => {
    if (visible) {
      getCausasPenalesData(true)
    }
  }, [visible])

  useEffect(() => {
    if (causasPenalesData) {
      if (!searchFilter.trim()) {
        setCausasPenales(causasPenalesData)
      } else {
        const searchTerm = searchFilter.toLowerCase().trim()
        
        const data = JSON.parse(JSON.stringify(causasPenalesData))
        const filtered = data.filter((causa, index) => {
          const causaMatches = causa.descripcion && causa.descripcion.toLowerCase().includes(searchTerm)
          const delitoMatches = causa.relatedDelito && Array.isArray(causa.relatedDelito) && causa.relatedDelito.some(delito => 
            delito.descripcion && delito.descripcion.toLowerCase().includes(searchTerm)
          )
          if (delitoMatches) {
            const delitos = causa.relatedDelito.filter(d => d.descripcion.toLowerCase().includes(searchTerm))
            causa.relatedDelito = delitos
          }
          
          return causaMatches || delitoMatches
        })

        setCausasPenales(filtered)
      }
    }
  }, [causasPenalesData, searchFilter])

  const getCausasPenalesData = async (loading = false) => {
    try {
      if (loading) setIsLoadingCausasPenales(true)
      const response = await getCausasPenalesDetailPromise()
      const data = response && response.categoriasDelitos ? response.categoriasDelitos : []
      
      setCausasPenalesData(data)
      
      if (response && response.lastUpdate) {
        setLastUpdate(response.lastUpdate)
      }
    } catch (error) {
      notification.error({
        message: t('messages.aml.notifications.anErrorOcurred'),
        description: 'Error al cargar las categorías de delitos. Por favor, inténtalo de nuevo.'
      })
      setCausasPenalesData([])
    } finally {
      setIsLoadingCausasPenales(false)
    }
  }

  const handleModalCancel = () => {
    setIsLoadingCausasPenales(true)
    setSearchFilter('')
    setExpandedKeys([])
    onCancel()
  }

  const handleSearchFilterChange = (value) => {
    setSearchFilter(value)
  }

  const handlePanelChange = (keys) => {
    setExpandedKeys(keys)
  }

  const getRiskLevelOptions = () => {
    return [
      { value: null, label: t('messages.aml.risk.NA'), color: '#d9d9d9' },
      { value: 'GREEN', label: t('messages.aml.risk.GREEN'), color: '#52c41a' },
      { value: 'YELLOW', label: t('messages.aml.risk.YELLOW'), color: '#fadb14' },
      { value: 'ORANGE', label: t('messages.aml.risk.ORANGE'), color: '#fa8c16' },
      { value: 'RED', label: t('messages.aml.risk.RED'), color: '#f5222d' },
      { value: 'BLACK', label: t('messages.aml.risk.BLACK'), color: '#000000' }
    ]
  }

  const getRiskLevelColor = (riskValue) => {
    const options = getRiskLevelOptions()
    const option = options.find(opt => opt.value === riskValue)
    return option ? option.color : '#fafafa'
  }

  const hasAnySubcategoryRiskMismatch = (causa) => {
    const causaRisk = causa.riesgo
    
    return causa.relatedDelito.some((delito) => {
      const delitoRisk = delito.riesgo
      return delitoRisk && delitoRisk !== causaRisk
    })
  }

  const handleCausaPenalToggle = async (causa, checked) => {    
    const itemKey = `causa_${causa.id}_toggle`
    setLoadingItems(prev => ({ ...prev, [itemKey]: true }))
    
    try {
      const params = {
        id: causa.id,
        estado: checked ? 'ACTIVE' : 'INACTIVE',
        riesgo: causa.riesgo,
        descripcion: causa.descripcion
      }

      const result = await updateCategoriaDelitoPromise(params)
      
      if (result && result.data) {
        getCausasPenalesData()
        
        notification.success({
          message: t('messages.aml.successfulOperation'),
          description: `Categoría ${checked ? 'activada' : 'desactivada'} exitosamente`
        })
      } else {
        throw new Error('No se recibió respuesta del servidor')
      }
    } catch (error) {
      console.error('Error actualizando estado de categoría:', error)
      notification.error({
        message: t('messages.aml.notifications.anErrorOcurred'),
        description: 'Error al actualizar el estado de la categoría: ' + (error.message || 'Error desconocido')
      })
    } finally {
      setLoadingItems(prev => ({ ...prev, [itemKey]: false }))
    }
  }

  const handleCausaRiskLevelChange = async (causa, value) => {
    if (causa.estado === 'INACTIVE') {
      notification.warning({
        message: t('messages.aml.information'),
        description: 'No se puede cambiar el riesgo de una categoría inactiva'
      })
      return
    }

    const itemKey = `causa_${causa.id}_risk`
    setLoadingItems(prev => ({ ...prev, [itemKey]: true }))

    try {
      const params = {
        id: causa.id,
        estado: causa.estado,
        riesgo: value,
        descripcion: causa.descripcion
      }

      const result = await updateCategoriaDelitoPromise(params)
      
      if (result && result.data) {
        getCausasPenalesData()
        
        notification.success({
          message: t('messages.aml.successfulOperation'),
          description: 'Nivel de riesgo actualizado exitosamente'
        })
      } else {
        throw new Error('No se recibió respuesta del servidor')
      }
    } catch (error) {
      console.error('Error actualizando nivel de riesgo:', error)
      notification.error({
        message: t('messages.aml.notifications.anErrorOcurred'),
        description: 'Error al actualizar el nivel de riesgo: ' + (error.message || 'Error desconocido')
      })
    } finally {
      setLoadingItems(prev => ({ ...prev, [itemKey]: false }))
    }
  }

  const handleDelitoRiskLevelChange = async (delito, value) => {
    const itemKey = `delito_${delito.id}_risk`
    setLoadingItems(prev => ({ ...prev, [itemKey]: true }))

    try {
      const params = {
        id: delito.id,
        estado: 'ACTIVE',
        riesgo: value,
        descripcion: delito.descripcion
      }

      const result = await updateCategoriaDelitoPromise(params)
      
      if (result && result.data) {
        getCausasPenalesData()
        
        notification.success({
          message: t('messages.aml.successfulOperation'),
          description: 'Nivel de riesgo del delito actualizado exitosamente'
        })
      } else {
        throw new Error('No se recibió respuesta del servidor')
      }
    } catch (error) {
      console.error('Error actualizando nivel de riesgo del delito:', error)
      notification.error({
        message: t('messages.aml.notifications.anErrorOcurred'),
        description: 'Error al actualizar el nivel de riesgo del delito: ' + (error.message || 'Error desconocido')
      })
    } finally {
      setLoadingItems(prev => ({ ...prev, [itemKey]: false }))
    }
  }

  const handleDownload = async (isTemplate = false) => {
    try {
      setIsDownloadingExcel(true)

      const result = await excelCategoriasDelitosPromise({ 
        listadoOficial: isTemplate 
      })
      
      if (result && result.data) {
        const blob = result.data
        if (blob.size === 0) {
          throw new Error('El archivo está vacío')
        }

        const url = window.URL.createObjectURL(blob)
        
        const link = document.createElement('a')
        link.href = url
        link.download = isTemplate ? 'plantilla_categorias_delitos.xlsx' : 'categorias_delitos_penales.xlsx'
        link.style.display = 'none'
        
        document.body.appendChild(link)
        link.click()
        
        notification.success({
          message: t('messages.aml.successfulOperation'),
          description: isTemplate ? 'Plantilla descargada exitosamente' : 'Excel descargado exitosamente'
        })
      } else {
        throw new Error('No se recibieron datos del servidor')
      }
    } catch (error) {
      console.error(`Error descargando ${isTemplate ? 'plantilla' : 'Excel'}:`, error)
      notification.error({
        message: t('messages.aml.notifications.anErrorOcurred'),
        description: `Error al descargar ${isTemplate ? 'la plantilla' : 'el archivo Excel'}: ` + (error.message || 'Error desconocido')
      })
    } finally {
      setIsDownloadingExcel(false)
    }
  }

  const handleUploadCategories = (file) => {
    const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                    file.type === 'application/vnd.ms-excel' ||
                    file.name.endsWith('.xlsx') || 
                    file.name.endsWith('.xls')
    
    if (!isExcel) {
      notification.error({
        message: t('messages.aml.notifications.anErrorOcurred'),
        description: 'Por favor selecciona un archivo Excel (.xlsx o .xls)'
      })
      return false
    }

    const maxSize = 500 * 1024
    if (file.size > maxSize) {
      notification.error({
        message: t('messages.aml.notifications.anErrorOcurred'),
        description: 'El archivo es demasiado grande. El tamaño máximo permitido es 500KB.'
      })
      return false
    }

    setSelectedFile(file)
    // notification.success({
    //   message: 'Archivo seleccionado',
    //   description: `Archivo "${file.name}" seleccionado correctamente. Presiona "Guardar Categorías" para procesarlo.`
    // })

    return false
  }

  const generatePDF = async () => {
    setIsGeneratingPDF(true)
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const margin = 25
      const contentWidth = pageWidth - (2 * margin)
      let yPosition = margin
      
      // Fila superior: fecha de descarga (izquierda) y última modificación (derecha)
      try {
        const now = new Date()
        const formatDateTime = (d) => {
          const pad = (n) => String(n).padStart(2, '0')
          const dd = pad(d.getDate())
          const mm = pad(d.getMonth() + 1)
          const yyyy = d.getFullYear()
          const hh = pad(d.getHours())
          const min = pad(d.getMinutes())
          return `${dd}/${mm}/${yyyy} ${hh}:${min}`
        }
        const downloadLabel = `Fecha de descarga: ${formatDateTime(now)}`
        const lastModLabel = `Última modificación: ${lastUpdate ? formatDateTime(new Date(lastUpdate)) : 'N/A'}`
        pdf.setFontSize(9)
        pdf.setFont(undefined, 'normal')
        pdf.setTextColor(102, 102, 102)

        pdf.text(lastModLabel, margin, yPosition, { align: 'left' })
        pdf.text(downloadLabel, pageWidth - margin, yPosition, { align: 'right' })
        yPosition += 14
      } catch (e) {
        // en caso de algo inesperado con fechas, continuamos sin bloquear el hilo principal
      }
      
      const addText = (text, fontSize = 12, isBold = false, color = '#000000', align = 'left', x = margin, maxWidth = contentWidth) => {
        pdf.setFontSize(fontSize)
        pdf.setTextColor(color)
        if (isBold) {
          pdf.setFont(undefined, 'bold')
        } else {
          pdf.setFont(undefined, 'normal')
        }
        
        const lines = pdf.splitTextToSize(text, maxWidth)
        if (yPosition + (lines.length * fontSize * 0.4) > pageHeight - margin) {
          pdf.addPage()
          yPosition = margin
        }
        
        pdf.text(lines, x, yPosition, { align })
        yPosition += lines.length * fontSize * 0.4 + 5
      }
      
      const addCircle = (x, y, radius, color) => {
        const rgbColor = hexToRgb(color)
        if (rgbColor) {
          pdf.setFillColor(rgbColor.r, rgbColor.g, rgbColor.b)
          pdf.circle(x, y, radius, 'F')
        }
      }
      
      const addRectangle = (x, y, width, height, color = '#f9f9f9', borderColor = '#000000') => {
        const fillRgb = hexToRgb(color)
        const borderRgb = hexToRgb(borderColor)
        
        if (fillRgb) {
          pdf.setFillColor(fillRgb.r, fillRgb.g, fillRgb.b)
        }
        if (borderRgb) {
          pdf.setDrawColor(borderRgb.r, borderRgb.g, borderRgb.b)
        }
        pdf.setLineWidth(0.5)
        pdf.rect(x, y, width, height, 'FD')
      }
      
      // Tabla usando jspdf-autotable con centrado y altura automática
      const addTable = (headers, data, startX, startY, tableWidth) => {
        autoTable(pdf, {
          head: [headers],
          body: data,
          startY: startY,
          margin: { left: margin, right: margin },
          theme: 'grid',
          styles: {
            halign: 'left',
            valign: 'middle',
            fontSize: 9,
            cellPadding: { top: 2, right: 5, bottom: 2, left: 5 },
            overflow: 'linebreak',
            textColor: [0, 0, 0],
            lineColor: [200, 200, 200],
            lineWidth: 0.25
          },
          headStyles: { fillColor: [240, 240, 240], halign: 'center', valign: 'middle', textColor: [0, 0, 0] },
          alternateRowStyles: { fillColor: [250, 250, 250] },
          tableWidth: tableWidth,
          columnStyles: {
            0: { cellWidth: tableWidth * 0.7, halign: 'left' },
            1: { cellWidth: tableWidth * 0.3, halign: 'center' }
          }
        })
        return pdf.lastAutoTable?.finalY || startY
      }
      
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : null
      }
      
      addText('Categorías de Delitos Penales', 22, true, '#333333', 'center', pageWidth / 2, contentWidth)
      addText('Las categorías de delitos son personales para cada cliente según su manual de prevención', 12, false, '#666666', 'center', pageWidth / 2, contentWidth)
      
      yPosition += 25
      
      causasPenalesData.forEach((causa, index) => {
        const isActive = causa.estado === 'ACTIVE'
        const riskOption = getRiskLevelOptions().find(opt => opt.value === causa.riesgo)
        
        addText(`${index + 1}. ${causa.descripcion || 'Delitos nuevos NO Categorizados'}`, 14, true, '#333333')
        
        const estadoText = isActive ? 'Activa' : 'Inactiva'
        const infoText = `Estado: ${estadoText} | Nivel de Riesgo: ${riskOption ? riskOption.label : 'N/A'}`
        addText(infoText, 10, false, '#666666')
        
        if (causa.relatedDelito && causa.relatedDelito.length > 0) {
          // addText('Delitos Relacionados:', 12, true, '#555555')
          
          // Preparar datos para la tabla
          const tableHeaders = ['Descripción del Delito', 'Nivel de Riesgo']
          const tableData = causa.relatedDelito.map((delito, delitoIndex) => {
            const delitoRiskOption = getRiskLevelOptions().find(opt => opt.value === delito.riesgo)
            return [
              delito.descripcion || 'Delito sin descripción',
              delitoRiskOption ? delitoRiskOption.label : 'N/A'
            ]
          })
          
          // Generar la tabla (autotable maneja saltos de página y alturas)
          yPosition = addTable(tableHeaders, tableData, margin, yPosition, contentWidth)
          
        } else {
          addText('No hay delitos relacionados', 10, false, '#999999', 'left', margin + 15)
        }
        
        yPosition += 20
      })
      
      pdf.save('Categorias_Delitos_Penales.pdf')
      
      notification.success({
        message: t('messages.aml.successfulOperation'),
        description: 'PDF generado y descargado exitosamente'
      })
      
    } catch (error) {
      console.error('Error generando PDF:', error)
      notification.error({
        message: t('messages.aml.notifications.anErrorOcurred'),
        description: 'Error al generar el PDF'
      })
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  const handleUploadModalOpen = () => {
    setUploadModalVisible(true)
  }

  const handleUploadModalCancel = () => {
    setUploadModalVisible(false)
    setUploadProgress(0)
    setIsUploadingCategories(false)
    setSelectedFile(null)
  }

  const handleSaveCategories = async () => {
    if (!selectedFile) {
      notification.error({
        message: t('messages.aml.notifications.anErrorOcurred'),
        description: 'Por favor selecciona un archivo antes de guardar'
      })
      return
    }

    Modal.confirm({
      title: 'Confirmar actualización de categorías',
      content: (
        <div>
          <p>Vas a reemplazar las categorías de delitos con el archivo {selectedFile.name}</p>
          <p style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '8px' }}>
            Esta acción reemplazará todas las categorías actuales.
          </p>
        </div>
      ),
      okText: 'Sí, actualizar categorías',
      cancelText: 'Cancelar',
      okType: 'danger',
      onOk: async () => {
        try {
          setIsUploadingCategories(true)
          setUploadProgress(0)

          const progressInterval = setInterval(() => {
            setUploadProgress(prev => Math.min(prev + 10, 90))
          }, 200)

          const formData = new FormData()
          formData.append('file', selectedFile)

          const result = await uploadCategoriasDelitosPromise(formData)
          
          clearInterval(progressInterval)
          setUploadProgress(100)

          if (result && result.data) {
            notification.success({
              message: t('messages.aml.successfulOperation'),
              description: 'Categorías actualizadas exitosamente'
            })
            
            await getCausasPenalesData(true)
            setUploadModalVisible(false)
            setSelectedFile(null)
          } else {
            throw new Error('No se recibió respuesta del servidor')
          }
        } catch (error) {
          console.error('Error subiendo categorías:', error)
          notification.error({
            message: t('messages.aml.notifications.anErrorOcurred'),
            description: error.message || 'Error desconocido'
          })
        } finally {
          setIsUploadingCategories(false)
          setUploadProgress(0)
        }
      }
    })
  }

  const formatLastUpdate = () => {
    if (!lastUpdate) return null
    
    const updateDate = new Date(lastUpdate)
    const currentDate = new Date()
    
    const monthsDiff = (currentDate.getFullYear() - updateDate.getFullYear()) * 12 + 
                       (currentDate.getMonth() - updateDate.getMonth())
    
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }
    
    const formattedDate = updateDate.toLocaleDateString('es-ES', options)
    const monthsValid = Math.max(0, monthsDiff)
    
    return {
      date: formattedDate,
      monthsValid: monthsValid
    }
  }

  return (
    <>
      <Modal
        visible={visible}
        onCancel={handleModalCancel}
        footer={null}
        width={1000}
        className="categoria-delito"
      >
        <div>
          <div className="modal-header">
            <h2>
              Categorías de Delitos Penales
            </h2>
            <h4>
              Las categorías de delitos son personales para cada cliente según su manual de prevención
            </h4>
          </div>
          <div className="search-section">
            <div className="search-container">
              <Input.Search
                placeholder="Buscar categoría..."
                value={searchFilter}
                onChange={(e) => handleSearchFilterChange(e.target.value)}
                allowClear
              />
              {causasPenales && causasPenalesData && (
                <div className="search-results-count">
                  {causasPenales.length} de {causasPenalesData.length} categorías encontradas
                </div>
              )}
            </div>
            <div className="actions-container">
              <Button 
                type="primary" 
                icon="reload"
                onClick={handleUploadModalOpen}
              >
                Actualizar Categorías
              </Button>
              <Button 
                type="primary" 
                icon={isGeneratingPDF ? 'loading' : 'printer'} 
                onClick={generatePDF}
                loading={isGeneratingPDF}
                disabled={isGeneratingPDF}
              >
                {isGeneratingPDF ? 'Generando PDF...' : 'Imprimir PDF'}
              </Button>
            </div>
          </div>
          {isLoadingCausasPenales ? (
            <div className="loading-container">
              <Spin size="large" />
              <div className="loading-text">Cargando categorías de delitos desde el servidor...</div>
              <div className="loading-subtext">
                Esto puede tomar unos segundos
              </div>
            </div>
          ) : causasPenalesData?.length === 0 ? (
            <div className="error-container">
              <Icon type="exclamation-circle" className="error-icon" />
              <div className="error-title">No se pudieron cargar las categorías</div>
              <div className="error-description">
                Hubo un problema al cargar los datos desde el servidor
              </div>
              <Button 
                type="primary" 
                icon="reload"
                onClick={() => getCausasPenalesData(true)}
              >
                Reintentar
              </Button>
            </div>
          ) : (
            <>
              {causasPenales?.length === 0 && searchFilter.trim() !== '' ? (
                <div className="no-results-container">
                  <Icon type="search" className="no-results-icon" />
                  <div className="no-results-title">No se encontraron resultados</div>
                  <div className="no-results-description">Intenta con otros términos de búsqueda</div>
                </div>
              ) : (
                <Collapse 
                  activeKey={expandedKeys}
                  onChange={handlePanelChange}
                  accordion
                  className="collapse-panel"
                >
                  {causasPenales.map((causa, index) => (
                    <Collapse.Panel 
                      key={causa.id || '0'} 
                      header={
                        <div className="panel-header">
                          <div className={`panel-title ${causa.estado === 'INACTIVE' ? 'inactive' : ''}`}>
                            {causa.descripcion || 'Delitos nuevos NO Categorizados'}
                            {causa.estado === 'INACTIVE' && (
                              <span className="inactive-label">
                                (Inactiva)
                              </span>
                            )}
                          </div>
                          <div 
                            className="panel-controls"
                            onClick={(e) => {
                              if (e && e.stopPropagation) {
                                e.stopPropagation()
                              }
                            }}
                          >
                            <span className="active-label">Activa</span>
                            <Tooltip title="Al desactivar la categoría, se deshabilitarán los dropdowns de riesgo">
                              <Switch
                                checked={causa.estado === 'ACTIVE'}
                                onChange={(checked) => handleCausaPenalToggle(causa, checked)}
                                size="small"
                                loading={loadingItems[`causa_${causa.id}_toggle`]}
                                disabled={loadingItems[`causa_${causa.id}_toggle`]}
                                onClick={(e) => {
                                  if (e && e.stopPropagation) {
                                    e.stopPropagation()
                                  }
                                }}
                              />
                            </Tooltip>
                            {hasAnySubcategoryRiskMismatch(causa) && (
                              <Tooltip title="Algunos delitos de esta categoría tienen otro nivel de riesgo">
                                <span className="warning-icon">
                                  ⚠️
                                </span>
                              </Tooltip>
                            )}
                            <Select
                              className={`risk-select ${causa.estado === 'INACTIVE' ? 'inactive' : ''}`}
                              size="small"
                              placeholder="Nivel de riesgo"
                              value={causa.riesgo}
                              onChange={(value) => handleCausaRiskLevelChange(causa, value)}
                              disabled={causa.estado === 'INACTIVE' || loadingItems[`causa_${causa.id}_risk`]}
                              loading={loadingItems[`causa_${causa.id}_risk`]}
                              onClick={(e) => {
                                if (e && e.stopPropagation) {
                                  e.stopPropagation()
                                }
                              }}
                              onDropdownVisibleChange={(visible) => {
                                if (visible) {
                                  setTimeout(() => {
                                    const event = new Event('click', { bubbles: false })
                                    event.stopPropagation = () => {}
                                    return false
                                  }, 0)
                                }
                              }}
                            >
                              {getRiskLevelOptions().map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                  <div className="risk-option">
                                    <div 
                                      className="risk-color"
                                      style={{ backgroundColor: option.color }}
                                    />
                                    {option.label}
                                  </div>
                                </Select.Option>
                              ))}
                            </Select>
                          </div>
                        </div>
                      }
                    >
                      {causa.relatedDelito && causa.relatedDelito.length > 0 ? (
                        <div>
                          {causa.relatedDelito.map((delito, delitoIndex) => (
                            <div 
                              key={delitoIndex} 
                              className={`delito-item ${causa.estado === 'INACTIVE' ? 'inactive' : ''}`}
                              style={{ 
                                border: `2px solid ${getRiskLevelColor(delito.riesgo)}`,
                                backgroundColor: causa.estado === 'ACTIVE' ? '#fafafa' : '#f5f5f5'
                              }}
                            >
                              <div className="delito-content">
                                <div className="delito-info">
                                  <div className="delito-name">
                                    {delito.descripcion || 'Delito sin descripción'}
                                  </div>
                                </div>
                                <div className="delito-controls">
                                  <Select
                                    className={`delito-risk-select ${causa.estado === 'ACTIVE' ? '' : 'inactive'}`}
                                    size="small"
                                    placeholder="Nivel de riesgo"
                                    value={delito.riesgo}
                                    onChange={(value) => handleDelitoRiskLevelChange(delito, value)}
                                    disabled={causa.estado !== 'ACTIVE' || loadingItems[`delito_${delito.id}_risk`]}
                                    loading={loadingItems[`delito_${delito.id}_risk`]}
                                  >
                                    {getRiskLevelOptions().map(option => (
                                      <Select.Option key={option.value} value={option.value}>
                                        <div className="risk-option">
                                          <div 
                                            className="risk-color"
                                            style={{ backgroundColor: option.color }}
                                          />
                                          {option.label}
                                        </div>
                                      </Select.Option>
                                    ))}
                                  </Select>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-delitos">
                          No hay delitos relacionados
                        </div>
                      )}
                    </Collapse.Panel>
                  ))}
                </Collapse>
              )}
            </>
          )}
          {formatLastUpdate() && (
            <div className="modal-footer">
              <div className="last-update-info">
                Fecha de última actualización: {formatLastUpdate().date} — Vigente por {formatLastUpdate().monthsValid} meses
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        visible={uploadModalVisible}
        onCancel={handleUploadModalCancel}
        footer={[
          <Button key="cancel" onClick={handleUploadModalCancel}>
            Cancelar
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            onClick={handleSaveCategories}
            loading={isUploadingCategories}
            disabled={!selectedFile || isUploadingCategories}
          >
            Guardar Categorías
          </Button>
        ]}
        width={500}
        title="Actualizar Categorías de Delitos"
        className="upload-modal"
        style={{ top: '6%' }}
      >
        <div>
          <div>
            <Row type="flex" justify="center">
              <Col>
                <Tooltip 
                  title="Descargue el archivo .xlsx con los delitos del PJUD y categorías vigentes."
                  placement="top"
                >
                  <Button 
                    type="dashed" 
                    icon="download" 
                    size="large"
                    className="template-button"
                    onClick={() => handleDownload(false)}
                    loading={isDownloadingExcel}
                    disabled={isDownloadingExcel}
                  >
                    {isDownloadingExcel ? 'Descargando...' : 'Categorías Vigentes'}
                  </Button>
                </Tooltip>
              </Col>
            </Row>
            <Row style={{marginTop:10}}>
              <Col style={{textAlign:'center'}}>
                Puede utilizar este archivo como base para reorganizar o ajustar las categorías según sus requerimientos.
              </Col>
            </Row>
            <Row style={{marginTop:10}}>
              <Upload.Dragger
                name="file"
                accept=".xlsx,.xls"
                beforeUpload={handleUploadCategories}
                showUploadList={false}
                disabled={isUploadingCategories}
                multiple={false}
                style={{ padding: '32px 16px' }}
              >
                <p className="ant-upload-drag-icon">
                  <Icon type="inbox" />
                </p>
                <p className="ant-upload-text">
                  {isUploadingCategories ? 'Subiendo archivo...' : 'Haz clic o arrastra un archivo aquí'}
                </p>
                <p className="ant-upload-hint">
                  Solo archivos Excel (.xlsx, .xls) - Máximo 500KB
                </p>
                <p className="ant-upload-hint" style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  Asegúrese de mantener el formato, columnas y nombres originales para evitar errores en la carga de datos.
                </p>
              </Upload.Dragger>
              
              {selectedFile && (
                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <Icon type="file-excel" style={{ color: '#52c41a', marginRight: '8px' }} />
                      <span style={{ fontWeight: '500' }}>Archivo seleccionado:</span>
                      <span style={{ marginLeft: '8px', color: '#666' }}>{selectedFile.name}</span>
                    </div>
                    <Button 
                      type="link" 
                      size="small"
                      onClick={() => setSelectedFile(null)}
                      style={{ color: '#ff4d4f' }}
                    >
                      <Icon type="close" />
                    </Button>
                  </div>
                </div>
              )}
            </Row>
          </div>
          
          {isUploadingCategories && (
            <div className="upload-progress">
              <Progress 
                percent={uploadProgress} 
                status={uploadProgress === 100 ? 'success' : 'active'}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
              <p className="progress-text">
                {uploadProgress === 100 ? '¡Completado!' : 'Subiendo archivo...'}
              </p>
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}

export default withTranslation()(CategoriaDelito)
